import { useEffect, useRef, useState } from "react";
import { logEvent } from "../../../helper/AppInsightsHelper";
import { PromptLibraryItem } from "../../../interfaces/slices.interfaces";
import {
  useCreatePromptPlaygroundShareLinkMutation,
  useExecuteQueryPromptMutation,
  useLazyGetPromptPlaygroundSharedDataQuery,
  useSavePromptPlaygroundUserSessionMutation
} from "../../../slices/AIHub.slice";
import { getErrorMessage } from "../../../Utils/js-common";
import { apiErrorMessages, messages } from "../AIPlayground.constants";
import { PromptPlaygroundSession, PromptPlaygroundState, SaveSessionDialogInput } from "../Interfaces";
import { cleanShareParamsFromUrl, copyToClipboard, generateShortShareUrl, getShareIdFromUrl } from "../Utils/shareUrl";

const defaultValues: SaveSessionDialogInput = {
  title: "",
  description: "",
  query: "",
  prompt: "",
  isActiveSchedule: false, // Default to false
  scheduleFrequency: "",
  isPublic: true // Defaulted to true for now
};

export const usePromptPlayground = (selectedPrompt?: PromptLibraryItem) => {
  const [state, setState] = useState<PromptPlaygroundState>({
    query: "",
    prompt: "",
    results: "",
    resultImages: [],
    sessionName: "",
    schedules: [],
    localSessionsData: [],
    isSessionNameValid: null,
    isSaveDialogOpen: false,
    isScheduleDialogOpen: false,
    isSaving: false,
    isSaved: false,
    isSavingSchedules: false,
    isSchedulesSaved: false,
    dialogInputData: defaultValues
  });

  const markdownRef = useRef<HTMLDivElement>(null);
  const hasLoadedShareData = useRef(false);

  const [saveSession] = useSavePromptPlaygroundUserSessionMutation();
  const [executeQueryPrompt, { isLoading: isExecuting }] = useExecuteQueryPromptMutation();
  const [createShareLink, { isLoading: isCreatingShareLink }] = useCreatePromptPlaygroundShareLinkMutation();
  const [getSharedData] = useLazyGetPromptPlaygroundSharedDataQuery();
  const [fieldFocused, setFieldFocused] = useState<{ title: boolean; description: boolean }>({
    title: false,
    description: false
  });
  const [shareToastMessage, setShareToastMessage] = useState<string | null>(null);
  const [shareToastType, setShareToastType] = useState<"success" | "error">("success");
  const [isSharedContent, setIsSharedContent] = useState(false);

  // Load share data from URL on initial mount
  useEffect(() => {
    if (hasLoadedShareData.current) return;

    const shareId = getShareIdFromUrl();
    if (shareId) {
      hasLoadedShareData.current = true;
      // Fetch shared data from backend using the share ID
      getSharedData(shareId)
        .unwrap()
        .then((shareData) => {
          setState((prev) => ({
            ...prev,
            query: shareData.query || "",
            prompt: shareData.prompt || "",
            results: shareData.results || "",
            resultImages: shareData.resultImages || []
          }));
          setIsSharedContent(true);
          logEvent("Shared prompt loaded from URL", "AI Hub", "usePromptPlayground", {
            shareId,
            hasQuery: !!shareData.query,
            hasPrompt: !!shareData.prompt,
            hasResults: !!shareData.results
          });
          // Clean up the URL after loading share data
          cleanShareParamsFromUrl();
        })
        .catch((error) => {
          console.error("Failed to load shared data:", error);
          cleanShareParamsFromUrl();
        });
    }
  }, [getSharedData]);

  useEffect(() => {
    // Skip if share data was loaded - don't overwrite with selectedPrompt
    if (hasLoadedShareData.current) return;

    if (selectedPrompt) {
      setState((prev) => ({
        ...prev,
        query: selectedPrompt.query,
        prompt: selectedPrompt.prompt
      }));
    }
  }, [selectedPrompt]);

  const mappedData = state.localSessionsData.map((item: PromptPlaygroundSession) => ({
    sessionType: item.sessionType,
    sessionName: item.sessionName,
    query: item.query,
    prompt: item.prompt
  }));

  const validateSessionName = (name: string) => {
    const isUnique = !mappedData.some((item) => item.sessionName.toLowerCase() === name.toLowerCase());
    setState((prev) => ({ ...prev, isSessionNameValid: isUnique }));
  };

  const handleSaveDialogOpen = () => {
    logEvent("Save Session dialog opened", "AI Hub - KQL Mode", "usePromptPlayground", {
      query: state.query,
      prompt: state.prompt
    });
    setState((prev) => ({
      ...prev,
      dialogInputData: {
        ...prev.dialogInputData,
        query: state.query, // Update dialog input data with current query
        prompt: state.prompt // Update dialog input data with current prompt
      }
    }));
  };

  const handleSaveDialogClose = () => {
    setState((prev) => ({
      ...prev,
      dialogInputData: defaultValues // Reset dialog input data when closing the dialog
    }));
    setFieldFocused({
      // Reset field focus states
      title: false,
      description: false
    });
  };

  const updateField = (field: keyof PromptPlaygroundState, value: any) => {
    if (field === "isSaveDialogOpen") {
      if (value) {
        handleSaveDialogOpen();
      } else {
        handleSaveDialogClose();
      }
    }
    setState((prev) => ({ ...prev, [field]: value }));
  };

  const executePrompt = async () => {
    setState((prev) => ({ ...prev, results: "", resultImages: [] }));
    if (!state.query.trim() || !state.prompt.trim()) {
      alert(messages.COMPLETE_FIELDS);
      return;
    }

    const payload = {
      query: state.query.trim(),
      prompt: state.prompt.trim()
    };
    try {
      logEvent("Run button clicked in Prompt Playground", "AI Hub - KQL Mode", "UsePromptPlayground", payload);
      const response = await executeQueryPrompt(payload);

      if ("data" in response) {
        // Success case - validate data structure
        const { text = "", images = [] } = response.data || {};
        setState((prev) => ({
          ...prev,
          results: text,
          resultImages: images
        }));
        const errorMessages = apiErrorMessages;
        if (errorMessages.some((errorMessage) => text?.toLowerCase().includes(errorMessage))) {
          logEvent("Run event API failure", "AI Hub - KQL Mode", "usePromptPlayground", [
            { query: state.query, prompt: state.prompt, error: text }
          ]);
        }
      } else {
        // Extract error message with better type safety
        const errorMessage = getErrorMessage(response.error, messages.DEFAULT_ERROR_MESSAGE);

        setState((prev) => ({
          ...prev,
          results: errorMessage,
          resultImages: []
        }));
        logEvent("Run event API failure", "AI Hub - KQL Mode", "usePromptPlayground", [
          { query: state.query, prompt: state.prompt, error: errorMessage }
        ]);
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        results: `Error: ${messages.DEFAULT_ERROR_MESSAGE}`,
        resultImages: []
      }));
      logEvent("Run event API failure", "AI Hub - KQL Mode", "usePromptPlayground", [
        { query: state.query, prompt: state.prompt, error: error instanceof Error ? error.message : String(error) }
      ]);
    }
  };

  const handleDialogInputChange = (name: keyof SaveSessionDialogInput, value: string | boolean) => {
    if (name === "isActiveSchedule") {
      if (value) {
        setState((prev) => ({
          ...prev,
          dialogInputData: {
            ...prev.dialogInputData,
            isActiveSchedule: true,
            scheduleFrequency: "monthly" // Default frequency to monthly if schedule is active
          }
        }));
      } else {
        setState((prev) => ({
          ...prev,
          dialogInputData: {
            ...prev.dialogInputData,
            isActiveSchedule: false,
            scheduleFrequency: "" // Reset frequency if schedule is not active
          }
        }));
      }
    } else {
      setState((prev) => ({
        ...prev,
        dialogInputData: {
          ...prev.dialogInputData, // Update the specific field in dialog input data
          [name]: value || ""
        }
      }));
    }
  };

  const handleShare = async () => {
    const shareData = {
      query: state.query,
      prompt: state.prompt,
      results: state.results,
      resultImages: state.resultImages
    };

    logEvent("Share button clicked in Prompt Playground", "AI Hub", "usePromptPlayground", {
      queryLength: state.query.length,
      promptLength: state.prompt.length,
      resultsLength: state.results.length
    });

    try {
      // Create share link via backend API to get a short ID
      const response = await createShareLink(shareData).unwrap();
      const shareUrl = generateShortShareUrl(response.shareId);

      const success = await copyToClipboard(shareUrl);
      if (success) {
        setShareToastType("success");
        setShareToastMessage("Share this link with others to view your prompt and results.");
        setTimeout(() => setShareToastMessage(null), 4000);
      } else {
        setShareToastType("error");
        setShareToastMessage("Failed to copy share link. Please try again.");
        setTimeout(() => setShareToastMessage(null), 4000);
      }
    } catch (error) {
      console.error("Failed to create share link:", error);
      setShareToastType("error");
      setShareToastMessage("Failed to create share link. Please try again.");
      setTimeout(() => setShareToastMessage(null), 4000);
    }
  };

  const dismissShareToast = () => {
    setShareToastMessage(null);
  };

  const dismissSharedBanner = () => {
    setIsSharedContent(false);
  };

  return {
    dismissShareToast,
    dismissSharedBanner,
    executePrompt,
    fieldFocused,
    handleDialogInputChange,
    handleShare,
    isCreatingShareLink,
    isExecuting,
    isSharedContent,
    mappedData,
    markdownRef,
    saveSession,
    setFieldFocused,
    shareToastMessage,
    shareToastType,
    state,
    updateField,
    validateSessionName
  };
};
